/**
 * PartnerCodeOfferDetails — bloc "Remise & Offres" pour les cartes de code
 * dans les espaces partenaires (Royalties & Affiliation).
 *
 * Miroir des colonnes "Remise" et "Offres" de la page
 * Catégories → Codes Promotionnels / Codes Parrainage.
 *
 * Props:
 * - discountType : "percentage" | "fixed"
 * - discountValue: number (remise principale du code)
 * - offers       : array<{ id, label, discount_type, discount_value,
 *                          service_product_names?: string[] }>
 * - accent       : couleur d'accent (rouge pour promo, violet pour parrainage)
 */
import React from "react";
import { Ticket } from "lucide-react";

const fmtNumber = (n, isPct) =>
  Number(n || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: isPct ? 0 : 2,
    maximumFractionDigits: 2,
  });

function OfferChip({ offer, accent }) {
  const isPct = offer.discount_type === "percentage";
  const val = fmtNumber(offer.discount_value, isPct);
  const suffix = isPct ? "%" : "€";
  const names = Array.isArray(offer.service_product_names)
    ? offer.service_product_names.filter(Boolean)
    : [];
  const scope = names.length === 0
    ? "Tous services"
    : (names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", "));
  const label = (offer.label || "").trim();
  const tooltip = `${label ? label + " · " : ""}${val} ${suffix} · ${names.length ? names.join(", ") : "Tous services"}`;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] text-zinc-700 border border-zinc-200 rounded-sm px-1.5 py-0.5 bg-zinc-50"
      title={tooltip}
    >
      <span
        className="h-1.5 w-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: accent }}
      />
      {label && (
        <span className="truncate max-w-[100px] text-zinc-600">{label}</span>
      )}
      <span className="font-mono font-semibold" style={{ color: accent }}>
        {val} {suffix}
      </span>
      <span className="text-zinc-500 truncate max-w-[130px]">· {scope}</span>
    </span>
  );
}

export default function PartnerCodeOfferDetails({
  discountType,
  discountValue,
  offers,
  accent = "#EF4135",
  testIdPrefix,
}) {
  const list = Array.isArray(offers) ? offers : [];
  const hasBase = Number(discountValue) > 0;
  if (!hasBase && list.length === 0) return null;
  const isPct = discountType === "percentage";
  const baseLabel = hasBase
    ? `${fmtNumber(discountValue, isPct)} ${isPct ? "%" : "€"}`
    : null;

  return (
    <div
      className="pt-2 border-t border-zinc-100"
      data-testid={testIdPrefix ? `${testIdPrefix}-offers` : undefined}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono flex items-center gap-1">
          <Ticket size={9} /> Remise &amp; offres
        </p>
        {baseLabel && (
          <span
            className="font-mono font-semibold text-sm"
            style={{ color: accent }}
            data-testid={testIdPrefix ? `${testIdPrefix}-base-discount` : undefined}
          >
            {baseLabel}
          </span>
        )}
      </div>
      {list.length === 0 ? (
        <p className="text-[11px] text-zinc-500 italic">
          Remise unique appliquée à tous les services.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1">
          {list.map((o, i) => (
            <OfferChip key={o.id || `offer-${i}`} offer={o} accent={accent} />
          ))}
        </div>
      )}
    </div>
  );
}
